import React, { ReactElement, useState } from "react";
import { ThemeProvider } from "styled-components";
import { Autoplay, Pagination } from "swiper";
import { SwiperSlide } from "swiper/react"; /* eslint-disable-line import/no-unresolved */

import Image from "components/Image";
import Section from "components/Section";
import { useBreakpoint } from "hooks";
import generateSrcSet, { commonSourceSets } from "style/generateSrcSet";
import { addEditAttributes } from "utils/episerver";

import {
    Wrapper,
    Heading,
    Text,
    Watermark,
    Categories,
    Published,
    Slides,
    SlideContent,
    SlideWrapper,
    ReadMore,
} from "./NewsCarousel.styled";
import NewsCarouselProps from "./NewsCarouselProps";

const NewsCarousel = ({
    items,
    identifier,
    className,
    l18n,
    editPropertyName,
}: NewsCarouselProps): ReactElement => {
    const [sliderPaused, setSliderPaused] = useState<boolean>(false);

    let watermarkSize: string;
    switch (useBreakpoint()) {
        case "XL":
            watermarkSize = "140";
            break;
        case "L":
        case "M":
            watermarkSize = "105";
            break;
        default:
            watermarkSize = "56";
    }

    return (
        <Section
            theme="lightgray"
            alignItems="center"
            id={identifier}
            key={identifier}
            className={className}
        >
            <Wrapper
                $sliderPaused={sliderPaused}
                {...addEditAttributes(editPropertyName)}
            >
                <Slides
                    slidesPerView={1}
                    pagination={{ clickable: true }}
                    loop={true}
                    speed={200}
                    autoplay={{
                        delay: 5000,
                        pauseOnMouseEnter: true,
                        disableOnInteraction: false,
                    }}
                    onAutoplayPause={() => {
                        setSliderPaused(true);
                    }}
                    onAutoplayResume={() => {
                        setSliderPaused(false);
                    }}
                    modules={[Autoplay, Pagination]}
                >
                    {items?.map((item, index) => {
                        if (item.image && item.image.url)
                            item.image.srcSet = generateSrcSet({
                                image: item.image.url,
                                focalPoint: item.image.focalPoint,
                                content: commonSourceSets.large,
                                maxWidth: item.image.uploadedWidth,
                                maxHeight: item.image.uploadedHeight,
                                format: "jpg",
                            });

                        return (
                            <SwiperSlide key={`slide-${index}`}>
                                <ThemeProvider
                                    theme={{ hasImage: !!item.image?.url }}
                                >
                                    <SlideWrapper>
                                        <SlideContent>
                                            {item.image?.url && (
                                                <Image
                                                    {...item.image}
                                                    darken={true}
                                                    cover={true}
                                                />
                                            )}
                                            {item.categories && (
                                                <Categories>
                                                    {item.categories}
                                                </Categories>
                                            )}
                                            {item.heading && (
                                                <Heading
                                                    as="h2"
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            item.heading || "",
                                                    }}
                                                />
                                            )}
                                            {item.watermark && (
                                                <Watermark
                                                    key={`Watermark-${
                                                        item.watermark
                                                    }-${watermarkSize || ""}`}
                                                    icon={`${item.watermark}${watermarkSize}`}
                                                />
                                            )}
                                            {item.startPublish &&
                                                !item.hideStartPublish && (
                                                    <Published>
                                                        {item.startPublish}
                                                    </Published>
                                                )}
                                            {item.introduction && (
                                                <Text as="div">
                                                    {item.introduction}
                                                </Text>
                                            )}
                                            {item.url && (
                                                <ReadMore
                                                    url={item.url}
                                                    actionTheme="white"
                                                >
                                                    {l18n?.readMore ||
                                                        "READ MORE"}
                                                </ReadMore>
                                            )}
                                        </SlideContent>
                                    </SlideWrapper>
                                </ThemeProvider>
                            </SwiperSlide>
                        );
                    })}
                </Slides>
            </Wrapper>
        </Section>
    );
};

export default React.memo(NewsCarousel);
